import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Box, IconButton, useTheme } from "@mui/material";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import AcxButton from "components/UI/AcxButton";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxMenu, { StyledMenuLabel } from "components/UI/Menu/AcxMenu";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { useStore } from "utils/useStore";
import { useCurrentDashboard } from "../../useCurrentDashboard";
import { useObjectMenu } from "../useObjectMenu";
import MontageCardStore from "./MontageCardStore";

export const MontageCardMenu = observer(({ store }: { store: MontageCardStore }) => {
    const currentDashboard = useCurrentDashboard();
    const messageStore = useStore(MessageStore);
    const theme = useTheme();
    const currentTitle = store.title?.trim() || store.data?.audioName || "";
    const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
    const [isDeleteLoading, setDeleteIsLoading] = useState(false);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [isEditLoading, setIsEditLoading] = useState(false);
    const [localTitle, setLocalTitle] = useState<string>(currentTitle);
    const {
        menuAnchor,
        isMenuOpen,
        setIsMenuOpen,
        handleMenuButtonClick,
        handleMenuButtonKeyDown,
    } = useObjectMenu();

    useEffect(() => {
        setLocalTitle(currentTitle);
    }, [currentTitle]);

    const handleDelete = async () => {
        if (!currentDashboard) {
            messageStore.logError("Failed to delete montage card");
            return;
        }
        try {
            setDeleteIsLoading(true);
            await currentDashboard.deleteSignalsObjectById(store.id);
            messageStore.logMessage("Card deleted", "success");
            setIsConfirmDeleteOpen(false);
        } catch (e) {
            messageStore.logError("Failed to delete montage card");
        } finally {
            setDeleteIsLoading(false);
        }
    };

    const handleSave = async () => {
        if (!currentDashboard) {
            messageStore.logError("Failed to update montage card");
            return;
        }

        const normalizedTitle = localTitle.trim();
        if (normalizedTitle === currentTitle) {
            setIsEditOpen(false);
            return;
        }

        try {
            setIsEditLoading(true);
            await currentDashboard.updateMontageCardTitle(
                store.id,
                normalizedTitle,
            );
            setIsEditOpen(false);
        } catch (_e) {
            // DashboardStore handles save error messaging and optimistic rollback.
        } finally {
            setIsEditLoading(false);
        }
    };

    const canConfigure = store.isEditable && store.isSaveable;
    const canDelete = store.isDeletable;

    if (!currentDashboard || (!canConfigure && !canDelete)) {
        return null;
    }

    return (
        <>
            <IconButton
                ref={menuAnchor}
                id={`${store.id}-montage-card-menu`}
                style={{
                    color: theme.palette.black.main,
                    height: "34px",
                }}
                onClick={handleMenuButtonClick}
                onKeyDown={handleMenuButtonKeyDown}
                size="small"
            >
                <MoreVertIcon />
            </IconButton>
            <AcxMenu
                open={isMenuOpen}
                anchorElement={isMenuOpen ? menuAnchor.current : undefined}
                onMenuClose={() => setIsMenuOpen(false)}
                disableAutoFocusItem
                menuItems={[
                    ...(canConfigure
                        ? [
                              {
                                  id: `${store.id}-edit-montage-card`,
                                  label: <StyledMenuLabel>Edit</StyledMenuLabel>,
                                  props: {
                                      onClick: () => {
                                          setIsEditOpen(true);
                                          setIsMenuOpen(false);
                                      },
                                  },
                              } as const,
                          ]
                        : []),
                    ...(canConfigure && canDelete ? (["Divider"] as const) : []),
                    ...(canDelete
                        ? [
                              {
                                  id: `${store.id}-delete-montage-card`,
                                  label: <StyledMenuLabel>Delete</StyledMenuLabel>,
                                  props: {
                                      onClick: () => {
                                          setIsConfirmDeleteOpen(true);
                                          setIsMenuOpen(false);
                                      },
                                  },
                              } as const,
                          ]
                        : []),
                ]}
            />
            <AcxDialog
                isOpen={isConfirmDeleteOpen}
                onClose={() => setIsConfirmDeleteOpen(false)}
                title="Confirm Delete Montage Card"
                dialogContentChildren={
                    <Box>Are you sure you want to delete this card?</Box>
                }
                maxWidth={false}
            >
                <AcxButton onClick={handleDelete} loading={isDeleteLoading}>
                    Delete
                </AcxButton>
            </AcxDialog>
            <AcxDialog
                isOpen={isEditOpen}
                onClose={() => setIsEditOpen(false)}
                title="Edit Montage"
                dialogContentChildren={
                    <Box sx={{ width: "400px", maxWidth: "75vw" }}>
                        <AcxMainTextField
                            id={`${store.id}-edit-name-input`}
                            labelText="Name"
                            value={localTitle}
                            onChange={(e) => setLocalTitle(e.target.value)}
                            fullWidth
                        />
                    </Box>
                }
            >
                <AcxButton onClick={handleSave} loading={isEditLoading}>
                    Save
                </AcxButton>
            </AcxDialog>
        </>
    );
});
